"use client"

import { useEffect } from "react"
import { useRouter } from "next/router"

import { useCovi } from "../contexts/CoviContext"
import { coviViewRegistry } from "../utils/viewContext"

/**
 * Drive the Sofia view-context registry from the app shell.
 *
 * Mount this once in `CoviMount` — alongside `useCoviHighlightTarget`, so it
 * survives client-side navigation for authenticated users. It owns the
 * registry's lifecycle:
 *
 * - starts the DOM observers on mount (and disconnects on unmount; the
 *   registry's session memory — the focus stack — survives a StrictMode
 *   remount because it lives on the singleton, not in the hook),
 * - keeps the registry's org slug current so routes serialize app-relative
 *   (`/{org}/simulated/x` → `/simulated/x`) and recent-view memory never
 *   crosses orgs,
 * - rescans on route changes as a belt to the MutationObserver — a shallow
 *   query-only navigation swaps no DOM, but the primary declaration may hang
 *   off the query (e.g. a drawer keyed by `?id=`).
 *
 * The registry itself is thread-independent and per-browser-tab; every chat
 * thread's `useCoviChat` snapshots it at send time.
 */
export function useCoviViewObserver(): void {
  const { orgSlug } = useCovi()
  const router = useRouter()

  useEffect(() => {
    coviViewRegistry.setOrgSlug(orgSlug)
  }, [orgSlug])

  useEffect(() => {
    coviViewRegistry.start()
    return () => {
      coviViewRegistry.stop()
    }
  }, [])

  useEffect(() => {
    coviViewRegistry.rescan()
  }, [router.asPath])
}
